import React, { useState, useEffect } from 'react';
import { Card, Button, Row, Col, Typography, Badge, Modal } from 'antd';
import { ShoppingCartOutlined, ArrowLeftOutlined, HeartOutlined, SafetyOutlined, BulbOutlined } from '@ant-design/icons';
import { useHistory } from 'react-router-dom';
import StripeCheckout from '../../components/checkout/StripeCheckout';
import { trackProductView, trackAddToCart, trackPurchase } from '../../../utils/analytics';
import squishyUnicornImage from '../../../assets/images/Squish Unicorn.png';

const { Title, Text, Paragraph } = Typography;

const CalmiSquishyUnicorn = () => {
  const history = useHistory();
  const [cartItems, setCartItems] = useState([]);
  const [checkoutVisible, setCheckoutVisible] = useState(false);

  useEffect(() => {
    trackProductView('lumi-squishy-unicorn', 'Lumi Squishy Unicorn', 499);
  }, []);

  const product = {
    id: 'lumi-squishy-unicorn',
    name: 'Lumi Squishy Unicorn',
    price: 499,
    image: squishyUnicornImage,
    collection: 'Squishy Collection',
    subtitle: 'Magical round dreamer',
    description: 'A magical, rounded unicorn in soft cream plush with pastel pink and blue mane accents. The chubby, squishy design makes this mystical friend irresistibly huggable.',
    fullDescription: 'Enter a world of squishy magic with the Lumi Squishy Unicorn. This adorable, round companion combines the wonder of a unicorn with the ultimate squeezability. Featuring soft cream plush with delicate pastel pink and blue mane accents, this mystical friend is perfect for children who love both fantasy and tactile comfort. The rounded design makes it incredibly huggable, while the magical unicorn features spark imagination and creativity. The ethereal chest light adds to the enchanting experience, making this the perfect companion for dreamy bedtime stories and magical daily adventures.',
    specs: { 
      weight: '2.0 lbs', 
      dimensions: '10" x 10" x 8"', 
      color: 'Cream Pink', 
      texture: 'Magic-Squishy Plush',
      batteryLife: '8+ hours',
      charging: 'USB-C',
      ageRange: '4-12 years'
    },
    features: [
      'Magical round unicorn design with spiral horn',
      'Soft cream plush with pastel pink and blue accents',
      'Ultra-squishy texture perfect for hugging and squeezing',
      'Ethereal white chest light for magical ambiance',
      'AI conversations focused on creativity and dreams',
      'Perfect size for both fidgeting and full hugs'
    ],
    idealFor: [
      'Children who love fantasy and magical themes',
      'Combining imagination with tactile comfort needs',
      'Bedtime stories and dream encouragement',
      'Stress relief through magical squeezing',
      'Encouraging creativity while providing sensory input',
      'Children who prefer softer, rounder companion shapes'
    ]
  };

  const addToCart = () => {
    trackAddToCart(product.id, product.name, product.price);
    setCartItems([{ ...product, quantity: 1 }]);
    setCheckoutVisible(true);
  };

  const handlePaymentSuccess = (paymentIntent) => {
    trackPurchase(paymentIntent.id, [{ id: product.id, name: product.name, price: product.price, quantity: 1 }], product.price);
    setCheckoutVisible(false);
    setCartItems([]);
    Modal.success({
      title: 'Preorder Confirmed! 🎉',
      content: (
        <div>
          <p>Your {product.name} preorder has been confirmed! It's guaranteed to arrive in summer 2027.</p>
          <p>You'll receive an email confirmation shortly.</p>
          <p><strong>Order ID:</strong> {paymentIntent.id}</p>
        </div>
      ),
    });
  };

  const handlePaymentError = (error) => {
    Modal.error({
      title: 'Payment Failed',
      content: 'There was an issue processing your payment. Please try again.',
    });
  };

  return (
    <div style={{ minHeight: '100vh', background: '#fff' }}>
      {/* Header */}
      <div style={{ 
        background: 'linear-gradient(135deg, #e6e6fa 0%, #dda0dd 100%)',
        padding: '20px 0',
        marginBottom: '40px'
      }}>
        <div style={{ maxWidth: '1200px', margin: '0 auto', padding: '0 20px' }}>
          <Button 
            type="text" 
            icon={<ArrowLeftOutlined />} 
            onClick={() => history.push('/')}
            style={{ color: 'white', marginBottom: '10px' }}
          >
            Back to Lumi Bears
          </Button>
          <div style={{ display: 'flex', alignItems: 'center' }}>
            <span style={{ fontSize: '32px', marginRight: '15px' }}>🦄</span>
            <div>
              <Title level={2} style={{ color: 'white', margin: 0 }}>
                {product.name}
              </Title>
              <Text style={{ color: 'rgba(255,255,255,0.8)', fontSize: '18px' }}>
                {product.subtitle}
              </Text>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div style={{ maxWidth: '1200px', margin: '0 auto', padding: '0 20px' }}>
        {/* Product Overview */}
        <Row gutter={[48, 32]} style={{ marginBottom: '60px' }}>
          <Col xs={24} lg={12}>
            <div style={{ 
              textAlign: 'center', 
              padding: '40px', 
              backgroundColor: '#f5f5f5', 
              borderRadius: '12px', 
              height: '500px', 
              display: 'flex', 
              alignItems: 'center', 
              justifyContent: 'center',
              border: '1px solid #e0e0e0'
            }}>
              <img 
                src={product.image} 
                alt={product.name}
                style={{ 
                  maxWidth: '100%', 
                  maxHeight: '100%', 
                  objectFit: 'contain',
                  borderRadius: '50%'
                }}
              />
            </div>
          </Col>
          <Col xs={24} lg={12}>
            <Badge.Ribbon text={product.collection} color="magenta">
              <Card>
                <Title level={1} style={{ marginTop: 0 }}>
                  {product.name}
                </Title>
                <Title level={2} style={{ color: '#e74c3c', marginBottom: '20px' }}>
                  ${product.price} <Text style={{ fontSize: '16px', color: '#7f8c8d' }}>Preorder · Guaranteed delivery in summer 2027</Text>
                </Title>
                <Paragraph style={{ fontSize: '18px', marginBottom: '30px' }}>
                  {product.fullDescription}
                </Paragraph>
                
                <Button
                  type="primary"
                  size="large"
                  icon={<ShoppingCartOutlined />}
                  onClick={addToCart}
                  block
                  style={{
                    background: '#e74c3c',
                    borderColor: '#e74c3c',
                    height: '60px',
                    fontSize: '20px',
                    fontWeight: 'bold',
                    marginBottom: '20px'
                  }}
                >
                  Preorder Now - ${product.price}
                </Button>

                <Card size="small" style={{ backgroundColor: '#f8f8ff' }}>
                  <Text strong>📅 Preorder Details:</Text>
                  <ul style={{ marginTop: '8px', marginBottom: 0 }}>
                    <li>✨ Preordering guarantees your Lumi in summer 2027</li>
                    <li>💳 Pay today to reserve yours</li>
                    <li>🔄 Cancel anytime before shipping</li>
                  </ul>
                </Card>
              </Card>
            </Badge.Ribbon>
          </Col>
        </Row>

        {/* Specifications & Features */}
        <Card title="Technical Specifications" style={{ marginBottom: '40px' }}>
          <Row gutter={[24, 16]}>
            {Object.entries(product.specs).map(([key, value]) => (
              <Col xs={12} md={6} key={key}>
                <Text strong>{key.charAt(0).toUpperCase() + key.slice(1).replace(/([A-Z])/g, ' $1')}:</Text><br/>
                <Text>{value}</Text>
              </Col>
            ))}
          </Row>
        </Card>

        <Row gutter={[32, 32]} style={{ marginBottom: '60px' }}>
          <Col xs={24} md={12}>
            <Card title="Key Features" style={{ height: '100%' }}>
              <ul>
                {product.features.map((feature, index) => (
                  <li key={index} style={{ marginBottom: '8px', fontSize: '16px' }}>
                    {feature}
                  </li>
                ))}
              </ul>
            </Card>
          </Col>
          <Col xs={24} md={12}>
            <Card title="Ideal For" style={{ height: '100%' }}>
              <ul>
                {product.idealFor.map((use, index) => (
                  <li key={index} style={{ marginBottom: '8px', fontSize: '16px' }}>
                    {use}
                  </li>
                ))}
              </ul>
            </Card>
          </Col>
        </Row>

        <Title level={2} style={{ textAlign: 'center', marginBottom: '40px' }}>
          Why Choose the Lumi Squishy Unicorn?
        </Title>
        <Row gutter={[32, 32]} style={{ marginBottom: '60px' }}>
          <Col xs={24} md={8}>
            <Card style={{ textAlign: 'center', height: '100%' }}>
              <HeartOutlined style={{ fontSize: '48px', color: '#e6e6fa', marginBottom: '16px' }} />
              <Title level={4}>Magical Squishiness</Title>
              <Text>
                Combines the wonder of unicorn magic with the ultimate squeezable comfort for both imagination and stress relief.
              </Text>
            </Card>
          </Col>
          <Col xs={24} md={8}>
            <Card style={{ textAlign: 'center', height: '100%' }}>
              <SafetyOutlined style={{ fontSize: '48px', color: '#dda0dd', marginBottom: '16px' }} />
              <Title level={4}>Dream Encouragement</Title>
              <Text>
                Perfect for bedtime stories and encouraging big dreams while providing the tactile comfort children crave.
              </Text>
            </Card>
          </Col>
          <Col xs={24} md={8}>
            <Card style={{ textAlign: 'center', height: '100%' }}>
              <BulbOutlined style={{ fontSize: '48px', color: '#da70d6', marginBottom: '16px' }} />
              <Title level={4}>Creative Comfort</Title>
              <Text>
                Sparks creativity and imagination while meeting sensory needs through magical, squeezable design.
              </Text>
            </Card>
          </Col>
        </Row>
      </div>

      <Modal
        title=""
        open={checkoutVisible}
        onCancel={() => setCheckoutVisible(false)}
        footer={null}
        width={900}
        style={{ top: 20 }}
        destroyOnClose
      >
        <StripeCheckout
          cartItems={cartItems}
          onPaymentSuccess={handlePaymentSuccess}
          onPaymentError={handlePaymentError}
        />
      </Modal>
    </div>
  );
};

export default CalmiSquishyUnicorn;